import classNames from 'classnames';
import { FC, useState } from 'react';

import { Button, ButtonVariant, Icon } from '@/shared/ui';

import { formatDate } from '@/helpers/dates';

import { ConditionView } from '../../ConditionView/ConditionView';
import { Typography } from '../../Typography';
import { BaseCard } from '../model/BaseCard/BaseCard';
import { PermitCardProps } from '../model/types';

import { DeletePermitButton } from './ActionButtons/DeletePermitButton';
import { DownloadExtractButton } from './ActionButtons/DownloadExtractButton';
import { Row, RowRenderer } from './RowRenderer';

export const PermitCard: FC<PermitCardProps> = ({
  data: { permissionName, permitNum, permitInfo, weapon },
  onDeletePermit,
  onDownloadExtract,
  variant,
}) => {
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const rows: Row[] = [
    {
      label: 'Номер дозволу',
      value: permitNum,
      dataTestId: 'permit-card-number',
    },
    {
      label: 'Дата видачі',
      value: formatDate(permitInfo.issuedAt),
      dataTestId: 'permit-card-date',
    },
    {
      label: 'Термін дії, до',
      value: formatDate(permitInfo.expiredAt),
      dataTestId: 'permit-card-expired-date',
    },
  ];
  const weaponRow: Row[] = [
    {
      label: 'Тип зброї',
      value: weapon.typeName,
      dataTestId: 'permit-card-weapon-type',
    },
    {
      label: 'Модель',
      value: weapon.model,
      dataTestId: 'permit-card-model',
    },
    {
      label: 'Серія та номер',
      value: weapon.serialNumber,
      dataTestId: 'permit-card-serial',
    },
    {
      label: 'Калібр',
      value: weapon.caliber,
      dataTestId: 'permit-card-caliber',
    },
  ];

  return (
    <BaseCard
      data-testid="permit-card"
      className={classNames('service-row-card', `service-row-card--${variant}`)}
      header={
        <Typography variant="H4" className="service-row-card__header">
          {permissionName}
        </Typography>
      }
      body={
        <>
          <div className="service-row-card__meta-wrapper">
            <RowRenderer rows={rows} />
          </div>
          <div
            className={classNames('service-row-card__details-container', {
              'service-row-card__details-container--expanded': showDetails,
            })}
          >
            <div className="service-row-card__meta-wrapper">
              <RowRenderer rows={weaponRow} />
            </div>

            {weapon.parts &&
              weapon.parts?.map((part, index) => (
                <div key={`${part.type}-${part.serialNum}`} className="service-row-card__meta-wrapper">
                  {index === 0 && (
                    <Typography className="service-row-card__meta-wrapper--header">Основні частини</Typography>
                  )}
                  <RowRenderer
                    rows={[
                      {
                        label: 'Тип',
                        value: part.type,
                        dataTestId: 'permit-card-typer',
                      },
                      {
                        label: 'Серія та номер',
                        value: part.serialNum,
                        dataTestId: 'permit-card-serial',
                      },
                      {
                        label: 'Калібр',
                        value: part.caliber,
                        dataTestId: 'permit-card-caliber',
                      },
                    ]}
                  />
                </div>
              ))}
          </div>
        </>
      }
      actions={[
        <Button
          key="detailed_btn"
          variant={ButtonVariant.GHOST}
          onClick={() => setShowDetails((prev) => !prev)}
          className="service-row-card__detailed-btn"
        >
          Детальніше
          <Icon
            name="arrow_drop_down"
            className={classNames('service-row-card__detailed-icon', {
              'service-row-card__detailed-icon--rotated': showDetails,
            })}
          />
        </Button>,
        <>
          <ConditionView key="download-extract" if={!!onDownloadExtract}>
            <DownloadExtractButton onClick={(): void => onDownloadExtract && onDownloadExtract()} />
          </ConditionView>
          <ConditionView key="delete-permit" if={!!onDeletePermit}>
            <DeletePermitButton
              onClick={(): void => onDeletePermit && onDeletePermit()}
              className="service-row-card__delete-permit"
            />
          </ConditionView>
        </>,
      ]}
    />
  );
};
